import {
  type HomeQuickAction,
  trackHomeQuickActionClicked,
  trackHomeQuickActionConfirmed,
} from '@src/analytics/EventTracker';
import {
  useUserIdentity,
  useUserStoryUploadFlow,
} from '@src/stores-v2/StoreContext';
import FileTextIcon from '@src/svgs/quick-actions/FileTextIcon';
import MailIcon from '@src/svgs/quick-actions/MailIcon';
import MessageSquareIcon from '@src/svgs/quick-actions/MessageSquareIcon';
import QuoteIcon from '@src/svgs/quick-actions/QuoteIcon';
import ScissorsIcon from '@src/svgs/quick-actions/ScissorsIcon';
import UploadIcon from '@src/svgs/quick-actions/UploadIcon';
import VideoIcon from '@src/svgs/quick-actions/VideoIcon';
import { ContentTab, Studio } from '@src/types.ts/general';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import { StoryPickerModal } from './StoryPickerModal';

import type { ReactNode } from 'react';

interface QuickActionItem {
  id: string;
  label: string;
  icon: ReactNode;
  isPrimary?: boolean;
}

const quickActions: QuickActionItem[] = [
  {
    id: 'generate-clips',
    label: 'Produce Clips',
    icon: <VideoIcon />,
    isPrimary: true,
  },
  {
    id: 'find-quotes',
    label: 'Find Quotes',
    icon: <QuoteIcon />,
  },
  {
    id: 'create-article',
    label: 'Create Articles',
    icon: <FileTextIcon />,
  },
  {
    id: 'social-posts',
    label: 'Social Posts',
    icon: <MessageSquareIcon />,
  },
  {
    id: 'create-email',
    label: 'Create Email',
    icon: <MailIcon />,
  },
  {
    id: 'edit-video',
    label: 'Edit Video',
    icon: <ScissorsIcon />,
  },
  {
    id: 'add-story',
    label: 'Add Story',
    icon: <UploadIcon />,
  },
];

const ACTION_CONFIG: Record<
  string,
  {
    title: string;
    tab?: ContentTab;
    route: Studio.content | Studio.creator;
    autoOpenProducer?: boolean;
  }
> = {
  'generate-clips': {
    title: "Let's produce some clips",
    tab: ContentTab.video_clip,
    route: Studio.content,
    autoOpenProducer: true,
  },
  'find-quotes': {
    title: "Let's find some quotes",
    tab: ContentTab.pull_quotes,
    route: Studio.content,
    autoOpenProducer: true,
  },
  'create-article': {
    title: "Let's write an article",
    tab: ContentTab.blog,
    route: Studio.content,
    autoOpenProducer: true,
  },
  'social-posts': {
    title: "Let's write a social post",
    tab: ContentTab.social_posts,
    route: Studio.content,
    autoOpenProducer: true,
  },
  'create-email': {
    title: "Let's write an email",
    tab: ContentTab.email,
    route: Studio.content,
    autoOpenProducer: true,
  },
  'edit-video': {
    title: 'Select a story to edit',
    route: Studio.creator,
  },
};

export const QuickActions = observer(() => {
  const [activeAction, setActiveAction] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openNewModal } = useUserStoryUploadFlow();
  const userIdentity = useUserIdentity();
  const {
    homeActionsEnableEmailButton,
    homeActionsEnableEditVideoButton,
    homeActionsEnableAddStoryButton,
  } = useFlagsCombination(userIdentity.currentRole);

  const visibleActions = useMemo(() => {
    const hiddenIds = new Set<string>();
    if (!homeActionsEnableEmailButton) hiddenIds.add('create-email');
    if (!homeActionsEnableEditVideoButton) hiddenIds.add('edit-video');
    if (!homeActionsEnableAddStoryButton) hiddenIds.add('add-story');
    return quickActions.filter(action => !hiddenIds.has(action.id));
  }, [
    homeActionsEnableEmailButton,
    homeActionsEnableEditVideoButton,
    homeActionsEnableAddStoryButton,
  ]);

  const handleActionClick = (actionId: string) => {
    trackHomeQuickActionClicked({
      action: actionId as HomeQuickAction,
      is_feature_flagged: ['create-email', 'edit-video', 'add-story'].includes(
        actionId,
      ),
    });
    if (actionId === 'add-story') {
      openNewModal(({ storyId }) => {
        const params = new URLSearchParams(searchParams);
        params.set('storyId', storyId);
        navigate(`/${Studio.dashboard}?${params.toString()}`);
      });
      return;
    }
    setActiveAction(actionId);
  };

  const handleConfirm = (storyId: string) => {
    const config = activeAction ? ACTION_CONFIG[activeAction] : null;
    if (!config) return;

    const destinationRoute =
      config.route === Studio.creator
        ? `/${Studio.creator}`
        : `/${Studio.content}`;

    trackHomeQuickActionConfirmed({
      action: activeAction as HomeQuickAction,
      story_id: storyId,
      destination_tab: config.tab,
      destination_route: destinationRoute,
    });

    const params = new URLSearchParams(searchParams);
    params.set('storyId', storyId);

    if (config.route === Studio.creator) {
      navigate(`/${Studio.creator}?${params.toString()}`);
    } else {
      if (config.tab) params.set('tab', config.tab);
      if (config.autoOpenProducer) params.set('produce', 'true');
      navigate(`/${Studio.content}?${params.toString()}`);
    }

    setActiveAction(null);
  };

  const activeConfig = activeAction ? ACTION_CONFIG[activeAction] : null;

  return (
    <Container data-testid="home-quick-actions">
      <ActionsRow>
        {visibleActions.map(action => (
          <ActionButton
            key={action.id}
            onClick={() => handleActionClick(action.id)}
            $isPrimary={action.isPrimary}
          >
            <IconWrapper $isPrimary={action.isPrimary}>
              {action.icon}
            </IconWrapper>
            <ActionLabel>{action.label}</ActionLabel>
          </ActionButton>
        ))}
      </ActionsRow>

      {activeConfig && (
        <StoryPickerModal
          isOpen={!!activeAction}
          onClose={() => setActiveAction(null)}
          title={activeConfig.title}
          onConfirm={handleConfirm}
        />
      )}
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 48px;
  width: 100%;
  box-sizing: border-box;
`;

const ActionsRow = styled.div`
  display: flex;
  gap: 32px;
  align-items: flex-start;
  flex-wrap: wrap;
  justify-content: center;

  @media (max-width: 900px) {
    gap: 24px;
  }

  @media (max-width: 600px) {
    gap: 16px;
  }
`;

const ActionButton = styled.button<{ $isPrimary?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
`;

const IconWrapper = styled.div<{ $isPrimary?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${props =>
    props.$isPrimary ? 'var(--primary)' : 'transparent'};
  border: ${props => (props.$isPrimary ? 'none' : '1px solid var(--border)')};
  color: ${props =>
    props.$isPrimary ? 'var(--background)' : 'var(--text-secondary)'};
  transition: background-color var(--transition-normal) ease;

  /* Hover fill — primary: canonical brand-green (--primary-hover);
   * non-primary: subtle border-tint. The value must be resolved per-instance
   * here in the main body: the ActionButton-hover selector below compiles to a
   * single static rule shared by every IconWrapper, so a prop-based value
   * placed directly in it collides across primary and non-primary instances
   * (last rule wins, which turned the primary button gray on hover). A CSS
   * custom property keeps it per-instance. */
  --icon-hover-bg: ${props =>
    props.$isPrimary
      ? 'var(--primary-hover)'
      : 'color-mix(in srgb, var(--border) 50%, transparent)'};

  ${ActionButton}:hover & {
    background-color: var(--icon-hover-bg);
  }

  @media (max-width: 600px) {
    width: 48px;
    height: 48px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const ActionLabel = styled.span`
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  white-space: nowrap;

  @media (max-width: 600px) {
    font-size: 11px;
  }
`;
